import { QueryClient, QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

type QueryDef<T> = { key: QueryKey; fn: QueryFunction<T>; opts?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> };

export const defaultQueryRetryCount = 3;
// 412 always reports a precondition of the request itself, so retrying cannot change the outcome.
const httpStatusesNotToRetry = [400, 401, 403, 404, 405, 410, 412, 429];

// Named so it stays reachable in tests: the jest setup replaces the client's own `retry` with `false`.
export const shouldRetryQuery = (failureCount: number, error: Error): boolean => {
  if (failureCount > defaultQueryRetryCount) {
    return false;
  }
  if (axios.isAxiosError(error) && httpStatusesNotToRetry.includes(error.response?.status ?? 0)) {
    console.log(`Aborting retry due to ${error.response?.status} status`);
    return false;
  }

  return true;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
  },
});

export function makeQuery<T>(
  key: QueryKey,
  fn: QueryFunction<T>,
  opts?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>,
): QueryDef<T> {
  return { fn, key, opts };
}

export function toReact<T>(def: QueryDef<T>): () => UseQueryResult<T> {
  return () => useQuery({ queryFn: def.fn, queryKey: def.key, ...def.opts });
}
